/**
 * Guided Session Recording Utility v2.3
 * 
 * Records and replays guided view sessions (state-based, not video).
 * 
 * Recording captures:
 * - Persona
 * - Mode (manual / auto)
 * - Step sequence
 * - Dwell time per step
 * - Route transitions
 * - Manual overrides (Back/Next)
 * - Timestamped events
 * 
 * Does NOT capture:
 * - Free navigation
 * - User input
 * - PII
 * - Screenshots or video
 */

/**
 * Session event types
 */
export const SESSION_EVENTS = {
  START: 'session_start',
  END: 'session_end',
  STEP_ENTER: 'step_enter',
  STEP_EXIT: 'step_exit',
  PERSONA_CHANGE: 'persona_change',
  MODE_CHANGE: 'mode_change',
  SPEED_CHANGE: 'speed_change',
  PLAY: 'play',
  PAUSE: 'pause',
  MANUAL_NEXT: 'manual_next',
  MANUAL_BACK: 'manual_back',
  AUTO_ADVANCE: 'auto_advance',
  LOOP_TOGGLE: 'loop_toggle'
};

/**
 * Create a new recording session
 */
export const createRecordingSession = ({
  userId,
  orgId,
  purpose = 'general',
  persona,
  mode,
  speed
}) => {
  return {
    id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    userId,
    orgId,
    purpose,
    initialState: {
      persona,
      mode,
      speed
    },
    events: [],
    metadata: {
      totalSteps: 0,
      totalDuration: 0,
      stepsVisited: []
    }
  };
};

/**
 * Add an event to the recording
 */
export const addRecordingEvent = (session, eventType, data = {}) => {
  const event = {
    type: eventType,
    timestamp: Date.now(),
    relativeTime: session.events.length > 0 
      ? Date.now() - new Date(session.createdAt).getTime()
      : 0,
    data
  };
  
  session.events.push(event);
  
  // Update metadata
  if (eventType === SESSION_EVENTS.STEP_ENTER) {
    session.metadata.totalSteps++;
    if (!session.metadata.stepsVisited.includes(data.stepIndex)) {
      session.metadata.stepsVisited.push(data.stepIndex);
    }
  }
  
  return session;
};

/**
 * Finalize a recording session
 */
export const finalizeRecording = (session) => {
  const endTime = Date.now();
  session.endedAt = new Date(endTime).toISOString();
  session.metadata.totalDuration = endTime - new Date(session.createdAt).getTime();
  
  // Calculate dwell times per step
  session.metadata.stepDwellTimes = calculateStepDwellTimes(session.events);
  
  return session;
};

/**
 * Calculate dwell time for each step
 */
const calculateStepDwellTimes = (events) => {
  const dwellTimes = {};
  let currentStep = null;
  let stepEnterTime = null;
  
  for (const event of events) {
    if (event.type === SESSION_EVENTS.STEP_ENTER) {
      if (currentStep !== null && stepEnterTime !== null) {
        if (!dwellTimes[currentStep]) dwellTimes[currentStep] = 0;
        dwellTimes[currentStep] += event.timestamp - stepEnterTime;
      }
      currentStep = event.data.stepIndex;
      stepEnterTime = event.timestamp;
    }
  }
  
  // Handle last step
  if (currentStep !== null && stepEnterTime !== null) {
    const lastEvent = events[events.length - 1];
    if (!dwellTimes[currentStep]) dwellTimes[currentStep] = 0;
    dwellTimes[currentStep] += lastEvent.timestamp - stepEnterTime;
  }
  
  return dwellTimes;
};

/**
 * Session Replay Engine
 */
export class SessionReplayEngine {
  constructor(session, onEvent) {
    this.session = session;
    this.onEvent = onEvent;
    this.currentEventIndex = 0;
    this.isPlaying = false;
    this.isPaused = false;
    this.playbackSpeed = 1;
    this.timeoutId = null;
    this.startTime = null;
  }
  
  play() {
    if (this.currentEventIndex >= this.session.events.length) {
      this.reset();
    }
    
    this.isPlaying = true;
    this.isPaused = false;
    this.startTime = Date.now();
    this.scheduleNextEvent();
  }
  
  pause() {
    this.isPaused = true;
    this.isPlaying = false;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
  
  reset() {
    this.pause();
    this.currentEventIndex = 0;
    this.startTime = null;
  }
  
  setSpeed(speed) {
    this.playbackSpeed = speed;
    if (this.isPlaying && !this.isPaused) {
      // Reschedule with new speed
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.scheduleNextEvent();
    }
  }
  
  scheduleNextEvent() {
    if (this.currentEventIndex >= this.session.events.length) {
      this.isPlaying = false;
      this.onEvent({ type: 'replay_complete' });
      return;
    }
    
    const currentEvent = this.session.events[this.currentEventIndex];
    const nextEvent = this.session.events[this.currentEventIndex + 1];
    
    // Emit current event
    this.onEvent(currentEvent);
    this.currentEventIndex++;
    
    if (nextEvent) {
      const delay = (nextEvent.relativeTime - currentEvent.relativeTime) / this.playbackSpeed;
      this.timeoutId = setTimeout(() => this.scheduleNextEvent(), Math.max(delay, 50));
    } else {
      this.isPlaying = false;
      this.onEvent({ type: 'replay_complete' });
    }
  }
  
  getProgress() {
    if (this.session.events.length === 0) return 0;
    return (this.currentEventIndex / this.session.events.length) * 100;
  }
  
  getCurrentTime() {
    if (this.currentEventIndex === 0) return 0;
    return this.session.events[this.currentEventIndex - 1]?.relativeTime || 0;
  }
  
  getTotalTime() {
    return this.session.metadata.totalDuration || 0;
  }
}

/**
 * Format duration for display
 */
export const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Session purposes
 */
export const SESSION_PURPOSES = {
  SALES: 'sales',
  RBI: 'rbi',
  TRAINING: 'training',
  AUDIT: 'audit',
  GENERAL: 'general'
};

export default {
  SESSION_EVENTS,
  SESSION_PURPOSES,
  createRecordingSession,
  addRecordingEvent,
  finalizeRecording,
  SessionReplayEngine,
  formatDuration
};
